import i18next from "i18next";
import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CustomTabs from "../Shared/CustomTabs";
import { getLabelFromValue } from "./settingsConstants";
import validSupergitHIS from "../../api/validSupergitHIS";
import ReusableTable from "../../components/Shared/CustomTable";

const TAB_ORDER = [
  "Cash Sale",
  "Cash Credit",
  "Insurance",
  "Corporate",
  "Government",
  "Pharmacy Cash",
  "Pharmacy Insurance",
];

// Category to tab mapping
const CATEGORY_TO_TAB = {
  insurance: "Insurance",
  corporate: "Corporate",
  government: "Government",
};

function NewSettingsTable({ data, title }) {
  const { t } = useTranslation();
  const lang = i18next.language;
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [payerList, setPayerList] = useState([]);

  useEffect(() => {
    if (title === "his_transactions_settings") {
      const fetchPayerList = async () => {
        try {
          const response = await validSupergitHIS.post("/his/getPayerList");
          if (response?.data?.data) {
            setPayerList(response.data.data);
          }
        } catch (error) {
          console.error("Error fetching payer list:", error);
        }
      };
      fetchPayerList();
    }
  }, [title]);

  const payerIdToCategoryMap = useMemo(() => {
    const map = {};
    payerList.forEach((payer) => {
      if (payer.payer_id && payer.category) {
        map[payer.payer_id] = payer.category;
      }
    });
    return map;
  }, [payerList]);

  // Tab mapping based on amount_type key patterns
  const getTabFromAmountType = (amountType) => {
    if (!amountType) return null;
    if (amountType.startsWith("cash_credit_")) return "Cash Credit";
    if (amountType.startsWith("insurance_")) return "Insurance";
    if (amountType.startsWith("coperate_")) return "Corporate";
    if (amountType.startsWith("government_")) return "Government";
    if (
      amountType.startsWith("cash_sale_") ||
      amountType.startsWith("advance_payment_") ||
      amountType.startsWith("deposit_use_") ||
      amountType.startsWith("total_patient_") ||
      amountType.endsWith("_medicine_cost")
    )
      return "Cash Sale";
    if (amountType.startsWith("cash_sales_pharma_")) return "Pharmacy Cash";
    if (
      amountType.startsWith("insurance_pharma_") ||
      amountType.startsWith("pharma_")
    )
      return "Pharmacy Insurance";

    const payerCategory = payerIdToCategoryMap[amountType];
    if (payerCategory) {
      return CATEGORY_TO_TAB[payerCategory] || null;
    }

    return null;
  };

  const groupedByTab = useMemo(() => {
    if (title !== "his_transactions_settings") return null;

    const groups = {};
    data?.forEach((transaction) => {
      const tab = getTabFromAmountType(transaction?.amount_type);
      if (tab) {
        if (!groups[tab]) groups[tab] = [];
        groups[tab].push(transaction);
      }
    });
    return groups;
  }, [data, title, payerIdToCategoryMap]);

  const availableTabs = useMemo(() => {
    if (!groupedByTab) return [];
    return TAB_ORDER.filter((tab) => groupedByTab[tab]?.length > 0);
  }, [groupedByTab]);

  const getAmountTypeDisplay = (amountType) => {
    if (!amountType) return "----------------";
    const cleanAmountType = amountType.startsWith("pharma_")
      ? amountType.replace("pharma_", "")
      : amountType;
    const payer = payerList.find((p) => p.payer_id === cleanAmountType);
    if (payer) {
      return `${payer.name} - ${t("Net Amount + VAT")}`;
    }
    return getLabelFromValue(amountType) || amountType;
  };

  const getRenderColumns = (showAmountType) => {
    const columns = [
      {
        field: "no",
        header: t("no"),
        body: (rowData, { rowIndex }) => rowIndex + 1,
        style: { width: "80px" },
        bodyCellClass: "!py-3",
      },
      {
        field: "account_code",
        header: t("Account Code"),
        body: (rowData) =>
          rowData?.account_type === "patient" ||
            rowData?.account_type === "company"
            ? "................."
            : rowData?.account_code,
        style: { width: "400px" },
        bodyCellClass: "!py-3",
      },
      {
        field: "account_name",
        header: lang === "ar" ? t("Account Name(AR)") : t("Account Name (EN)"),
        body: (rowData) =>
          rowData?.account_type === "patient" ||
            rowData?.account_type === "company"
            ? "................."
            : lang === "ar"
              ? rowData?.account_name_ar
              : rowData?.account_name_en,
        style: { width: "400px" },
        bodyCellClass: "!py-3",
      },
    ];

    if (showAmountType) {
      columns.push({
        field: "amount_type",
        header: t("Amount Type"),
        body: (rowData) =>
          title === "his_transactions_settings"
            ? getAmountTypeDisplay(rowData?.amount_type)
            : rowData?.amount_type || "----------------",
        bodyCellClass: "!py-3",
      });
    }

    return columns;
  };

  const getDefaultColumns = () => {
    const columns = [
      {
        field: "no",
        header: t("no"),
        body: (rowData, { rowIndex }) => rowIndex + 1,
        style: { width: "80px" },
        bodyCellClass: "!py-3",
      },
      {
        field: "account_type",
        header: t("Account Type"),
        body: (rowData) => rowData?.account_type?.slice(0, 1).toUpperCase() + rowData?.account_type?.slice(1),
        style: { width: "400px" },
        bodyCellClass: "!py-3",
      },
      {
        field: "account_code",
        header: t("Account Code"),
        body: (rowData) =>
          rowData?.account_type === "patient" ||
            rowData?.account_type === "company"
            ? "................."
            : rowData?.account_code,
        style: { width: "400px" },
        bodyCellClass: "!py-3",
      },
      {
        field: "account_name",
        header: lang === "ar" ? t("Account Name(AR)") : t("Account Name (EN)"),
        body: (rowData) =>
          rowData?.account_type === "patient" ||
            rowData?.account_type === "company"
            ? "................."
            : lang === "ar"
              ? rowData?.account_name_ar
              : rowData?.account_name_en,
        style: { width: "400px" },
        bodyCellClass: "!py-3",
      },
      {
        field: "amount_type",
        header: t("Amount Type"),
        body: (rowData) => title === "his_transactions_settings"
          ? getLabelFromValue(rowData?.amount_type)
          : rowData?.amount_type || "----------------",
        bodyCellClass: "!py-3",
      }
    ];
    return columns;
  }

  const renderTable = (tableData, showAmountType = true) => (
    <div>
      <ReusableTable
        data={tableData}
        columns={getRenderColumns(showAmountType)}
        loading={!tableData}
        paginator={false}
      />
    </div>
  );

  if (title === "his_transactions_settings" && availableTabs.length > 0) {
    const hisTabs = availableTabs.map((tabName) => ({
      name: t(tabName),
      content: renderTable(groupedByTab[tabName], true),
    }));

    return (
      <CustomTabs
        tabs={hisTabs}
        // className="gl-settings-tabs"
        activeTabIndex={activeTabIndex}
        setActiveTabIndex={setActiveTabIndex}
      />
    );
  }


  return (
    <div>
      <ReusableTable
        data={data}
        columns={getDefaultColumns()}
        loading={!data}
        paginator={false}
      />
    </div>
  );
}

export default NewSettingsTable;
